import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog, .frenzy-modal');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * Preloads the first visible image before swapping to avoid a blank flash,
   * and lazy-loads off-screen images so they don't compete for bandwidth.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Optimize images in the new gallery before inserting
    const images = newMediaGallery.querySelectorAll('.product-media__image, .product-media img');
    let firstImage = null;

    images.forEach((img, i) => {
      if (!(img instanceof HTMLImageElement)) return;
      img.decoding = 'async';
      if (i === 0) {
        // First image: load eagerly with high priority
        img.loading = 'eager';
        img.fetchPriority = 'high';
        firstImage = img;
      } else {
        // Remaining images: lazy load so they don't block the first
        img.loading = 'lazy';
      }
    });

    // If first image is available, preload it then swap; otherwise swap immediately
    if (firstImage && firstImage.src) {
      const preload = new Image();
      // sizes must be copied along with srcset: without it the preload defaults
      // to 100vw and fetches the largest candidate, warming the wrong cache entry
      if (firstImage.sizes) preload.sizes = firstImage.sizes;
      if (firstImage.srcset) preload.srcset = firstImage.srcset;
      preload.src = firstImage.src;

      const doSwap = () => this.replaceWith(newMediaGallery);

      if (preload.complete) {
        doSwap();
      } else {
        // Swap after image loads, or after 500ms timeout (whichever comes first)
        let swapped = false;
        const swap = () => {
          if (swapped) return;
          swapped = true;
          doSwap();
        };
        preload.onload = swap;
        preload.onerror = swap;
        setTimeout(swap, 500);
      }
    } else {
      this.replaceWith(newMediaGallery);
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
